import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import Name from 'components/Form/Name';
import Country from 'components/Form/Country';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { Dispatch } from 'redux';
import { ForeignBusinessVenturesItemModel } from 'forms/sections/foreign/business/ventures/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { isPVQForm } from 'helpers/form';
import PVQName from 'components/Form/PVQName';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import NotApplicable from 'components/Form/NotApplicable';
import RadioGroup from 'components/Form/RadioGroup';
import { foreignBusinessAffairsRelationshipOptions } from 'constants/enums/foreignBusinessOptions';
import Radio from 'components/Form/Radio';
import Checkbox from 'components/Form/Checkbox';
import { ApplicableModel, CountryArrayModel, CountryModel, LocationModel } from 'forms/shared/types';
import { isSafeInternational } from 'helpers/locations';
import { foreignBusinessInvolvementOptions } from 'constants/enums/foreignActivityOptions';

interface Props extends Partial<ForeignBusinessVenturesItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  formType?: string
}

export default class VenturesItem extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,

      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignBusinessVenturesItemModel>) => {
    this.props.onUpdate({
      Description: this.props.Description,
      Name: this.props.Name,
      Address: this.props.Address,
      Citizenship: this.props.Citizenship,
      Relationship: this.props.Relationship,
      Dates: this.props.Dates,
      Association: this.props.Association,
      Position: this.props.Position,
      Service: this.props.Service,
      Support: this.props.Support,
      Compensation: this.props.Compensation,
      AddressNotApplicable: this.props.AddressNotApplicable, // PVQ Only
      InUsaNotApplicable: this.props.InUsaApplicable, // PVQ Only
      CitizenshipNotApplicable: this.props.CitizenshipNotApplicable, // PVQ Only
      RelationshipPvq: this.props.RelationshipPvq, // PVQ Only
      RelationshipOtherExplanation: this.props.RelationshipOtherExplanation, // PVQ Only
      Role: this.props.Role, // PVQ Only
      RoleOtherExplanation: this.props.RoleOtherExplanation, // PVQ Only
      FinancialSupportEstimated: this.props.FinancialSupportEstimated, // PVQ Only
      ...queue,
    });
  };

  updateDescription = (values: ForeignBusinessVenturesItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateName = (values: ForeignBusinessVenturesItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateAddress = (values: ForeignBusinessVenturesItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateCitizenship = (values: ForeignBusinessVenturesItemModel['Citizenship']) => {
    this.update({
      Citizenship: values,
    });
  };

  updateRelationship = (values: ForeignBusinessVenturesItemModel['Relationship']) => {
    this.update({
      Relationship: values,
    });
  };

  updateDates = (values: ForeignBusinessVenturesItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateAssociation = (values: ForeignBusinessVenturesItemModel['Association']) => {
    this.update({
      Association: values,
    });
  };

  updatePosition = (values: ForeignBusinessVenturesItemModel['Position']) => {
    this.update({
      Position: values,
    });
  };

  updateService = (values: ForeignBusinessVenturesItemModel['Service']) => {
    this.update({
      Service: values,
    });
  };

  updateSupport = (values: ForeignBusinessVenturesItemModel['Support']) => {
    this.update({
      Support: values,
    });
  };

  updateCompensation = (values: ForeignBusinessVenturesItemModel['Compensation']) => {
    this.update({
      Compensation: values,
    });
  };

  updateAddressNotApplicable = (
    addressNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<ForeignBusinessVenturesItemModel> = {
      AddressNotApplicable: { applicable: addressNotApplicable.applicable }
    };

    const Address: LocationModel | undefined = this.props.Address;

    let country: CountryModel | string | CountryArrayModel | undefined = Address?.country;

    if (country && !addressNotApplicable.applicable) {
      if (isSafeInternational(Address)) {
        country = { value: 'INTERNATIONAL' } as CountryModel;
      }

      queue.Address = { country } as LocationModel;
    }

    this.update(queue);
  };

  updateInUsaNotApplicable = (
    inUsaNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<ForeignBusinessVenturesItemModel> = {
      InUsaNotApplicable: { applicable: inUsaNotApplicable.applicable }
    };

    if (!inUsaNotApplicable.applicable) {
      queue.Address = {} as LocationModel;
    }

    this.update(queue);
  };

  updateCitizenshipNotApplicable = (
    value: ForeignBusinessVenturesItemModel['CitizenshipNotApplicable']
  ) => {
    this.update({
      Citizenship: value?.applicable ? this.props.Citizenship : {},
      CitizenshipNotApplicable: value,
    });
  };

  updateRelationshipPvq = (value: ForeignBusinessVenturesItemModel['RelationshipPvq']) => {
    this.update({
      RelationshipPvq: value,
      RelationshipOtherExplanation: value.value === 'Other' ? this.props.RelationshipOtherExplanation : {},
    });
  };

  updateRelationshipOtherExplanation = (value: ForeignBusinessVenturesItemModel['RelationshipOtherExplanation']) => {
    this.update({
      RelationshipOtherExplanation: value,
    });
  };

  updateRole = (value: ForeignBusinessVenturesItemModel['Role']) => {
    this.update({
      Role: value,
      RoleOtherExplanation: value.value === 'Other' ? this.props.RoleOtherExplanation : {},
    });
  };

  updateRoleOtherExplanation = (value: ForeignBusinessVenturesItemModel['RoleOtherExplanation']) => {
    this.update({
      RoleOtherExplanation: value,
    });
  };

  updateFinancialSupportEstimated = (value: ForeignBusinessVenturesItemModel['FinancialSupportEstimated']) => {
    this.update({
      FinancialSupportEstimated: value
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="ventures-item">
        <Field
          title={i18n.t('foreign.pvq.business.ventures.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            aria-label={i18n.t('foreign.pvq.business.ventures.heading.name')}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="ventures-name"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
          adjustFor="address"
        >
          <PVQLocationUnknown
            {...this.props.Address}
            addressUnknown={this.props.AddressNotApplicable}
            addressUnknownLabel={i18n.t('foreign.pvq.business.ventures.label.idk')}
            inUsaUnknown={this.props.InUsaNotApplicable}
            inUsaUnknownLabel={i18n.t('foreign.pvq.business.ventures.label.idk')}
            onUpdateAddressUnknown={this.updateAddressNotApplicable}
            onUpdateInUsaUnknown={this.updateInUsaNotApplicable}
            showAddressUnknown
            showInUsaUnknown
            attribute={`${attribute}.Address`}
            name="foreign-venture-address"
            onUpdate={this.updateAddress} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.props.onError}
            required={this.props.required}
            geocode
            showMilitaryInstallation={false}
            label={i18n.t(`foreign.pvq.business.ventures.heading.address`)}
            domesticTitle={i18n.t(`foreign.pvq.business.ventures.heading.addressDomesticTitle`)}
            foreignTitle={i18n.t(`foreign.pvq.business.ventures.heading.addressForeignTitle`)}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.ventures.heading.citizenship')}
          help="foreign.business.ventures.help.citizenship"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
        >
          <NotApplicable
            {...this.props.CitizenshipNotApplicable}
            name="CitizenshipNotApplicable"
            className="foreign-venture-citizenship-not-applicable"
            onUpdate={this.updateCitizenshipNotApplicable}
            onError={this.props.onError}
            or={i18n.m('foreign.pvq.business.ventures.para.or')}
            label={i18n.t('foreign.pvq.business.ventures.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.CitizenshipNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Citizenship?.value)}
          >
            <Country
              {...this.props.Citizenship}
              name="Citizenship"
              onUpdate={this.updateCitizenship}
              onError={this.props.onError}
              className="ventures-citizenship"
              multiple
              required={this.props.required}
              attribute={`${attribute}.Citizenship`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.ventures.heading.relationshipPvq')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RelationshipPvq`}
        >
          <RadioGroup
            className="relationship-pvq option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.RelationshipPvq?.value}
            attribute={`${attribute}.RelationshipPvq`}
          >
            {foreignBusinessAffairsRelationshipOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateRelationshipPvq}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.RelationshipPvq?.value === 'Other'
          && (
            <Field
              title={i18n.t('foreign.pvq.business.ventures.heading.explanation')}
              optional
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.RelationshipOtherExplanation`}
            >
              <Textarea
                {...this.props.RelationshipOtherExplanation}
                name="RelationshipOtherExplanation"
                attribute={`${attribute}.RelationshipOtherExplanation`}
                onUpdate={this.updateRelationshipOtherExplanation}
                onError={this.props.onError}
                className="venture-relationship-other-explanation"
                required={this.props.required}
              />
            </Field>
          )}

        <Field
          title={i18n.t('foreign.pvq.business.ventures.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            {...this.props.Description}
            name="Description"
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            className="ventures-description"
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.ventures.heading.dates')}
          help="foreign.business.ventures.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            className="ventures-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.ventures.heading.role')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Role`}
        >
          <RadioGroup
            className="role option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.Role?.value}
            attribute={`${attribute}.Role`}
          >
            {foreignBusinessInvolvementOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateRole}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.Role?.value === 'Other'
          && (
            <Field
              title={i18n.t('foreign.pvq.business.ventures.heading.explanation')}
              optional
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.RoleOtherExplanation`}
            >
              <Textarea
                {...this.props.RoleOtherExplanation}
                name="RoleOtherExplanation"
                attribute={`${attribute}.RoleOtherExplanation`}
                onUpdate={this.updateRoleOtherExplanation}
                onError={this.props.onError}
                className="venture-role-other-explanation"
                required={this.props.required}
              />
            </Field>
          )}

        {this.props.Role?.value === 'I provided financial support' && (
          <Field
            title={i18n.t('foreign.pvq.business.ventures.heading.support')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Support`}
          >
            <Text
              name="Support"
              {...this.props.Support}
              onUpdate={this.updateSupport}
              onError={this.props.onError}
              className="ventures-support"
              required={this.props.required}
              attribute={`${attribute}.Support`}
              exactAtrribute
            />
            <Checkbox
              label={i18n.t('foreign.pvq.business.ventures.heading.financialSupportEstimated')}
              value="Yes"
              checked={this.props.FinancialSupportEstimated?.checked}
              className="financial-support-estimated"
              onUpdate={this.updateFinancialSupportEstimated}
              onError={this.props.onError}
            />
          </Field>
        )}

        <Field
          title={i18n.t('foreign.pvq.business.ventures.heading.compensation')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Compensation`}
        >
          <Textarea
            {...this.props.Compensation}
            name="Compensation"
            onUpdate={this.updateCompensation}
            onError={this.props.onError}
            className="ventures-compensation"
            required={this.props.required}
            attribute={`${attribute}.Compensation`}
          />
        </Field>
      </div>
    ) : (
      <div className="ventures-item">
        <Field
          title={i18n.t('foreign.business.ventures.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            {...this.props.Name}
            aria-label="Provide the full name of this foreign national"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="ventures-name"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.address')}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            className="ventures-address"
            layout={Location.ADDRESS}
            showPostOffice
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            geocode
            required={this.props.required}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.citizenship')}
          help="foreign.business.ventures.help.citizenship"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
        >
          <Country
            name="Citizenship"
            {...this.props.Citizenship}
            onUpdate={this.updateCitizenship}
            onError={this.props.onError}
            className="ventures-citizenship"
            multiple
            required={this.props.required}
            attribute={`${attribute}.Citizenship`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            className="ventures-description"
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.relationship')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Relationship`}
        >
          <Textarea
            name="Relationship"
            {...this.props.Relationship}
            onUpdate={this.updateRelationship}
            onError={this.props.onError}
            className="ventures-relationship"
            required={this.props.required}
            attribute={`${attribute}.Relationship`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.dates')}
          help="foreign.business.ventures.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            className="ventures-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.association')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Association`}
        >
          <Textarea
            name="Association"
            {...this.props.Association}
            onUpdate={this.updateAssociation}
            onError={this.props.onError}
            className="ventures-association"
            required={this.props.required}
            attribute={`${attribute}.Association`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.position')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Position`}
        >
          <Text
            name="Position"
            {...this.props.Position}
            onUpdate={this.updatePosition}
            onError={this.props.onError}
            className="ventures-position"
            required={this.props.required}
            attribute={`${attribute}.Position`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.service')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Service`}
        >
          <Text
            name="Service"
            {...this.props.Service}
            onUpdate={this.updateService}
            onError={this.props.onError}
            className="ventures-service"
            required={this.props.required}
            attribute={`${attribute}.Service`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.support')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Support`}
        >
          <Text
            name="Support"
            {...this.props.Support}
            onUpdate={this.updateSupport}
            onError={this.props.onError}
            className="ventures-support"
            required={this.props.required}
            attribute={`${attribute}.Support`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.ventures.heading.compensation')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Compensation`}
        >
          <Textarea
            name="Compensation"
            {...this.props.Compensation}
            onUpdate={this.updateCompensation}
            onError={this.props.onError}
            className="ventures-compensation"
            required={this.props.required}
            attribute={`${attribute}.Compensation`}
          />
        </Field>
      </div>
    );
  }
}

VenturesItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode) => arr,
  AddressNotApplicable: { applicable: true }, // PVQ Only
  InUsaApplicable: { applicable: true }, // PVQ Only
  CitizenshipNotApplicable: { applicable: true }, // PVQ Only
  RelationshipPvq: {}, // PVQ Only
  RelationshipOtherExplanation: {}, // PVQ Only
  Role: {}, // PVQ Only
  RoleOtherExplanation: {}, // PVQ Only
  FinancialSupportEstimated: {}, // PVQ Only
};
